import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { login, isAuthenticated, adminUser } = useAuth();

  useEffect(() => {
    // 既に認証済みの場合はロールに応じてリダイレクト
    if (isAuthenticated && adminUser) {
      const redirectPath =
        adminUser.role === 'admin' ? '/admin/access-logs' : '/admin/companies';
      window.location.href = redirectPath;
    }
  }, [isAuthenticated, adminUser]);

  useEffect(() => {
    // ログイン成功後、adminUserが設定されたらリダイレクト
    if (loginSuccess && adminUser) {
      const redirectPath =
        adminUser.role === 'admin' ? '/admin/access-logs' : '/admin/companies';
      window.location.href = redirectPath;
    }
  }, [loginSuccess, adminUser]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setLoginSuccess(false);

    try {
      const success = await login(email, password);

      if (success) {
        setLoginSuccess(true);
        // adminUserが設定されるまで待つ（useEffectでリダイレクト）
      } else {
        setError(
          'ログイン情報が正しくありません。メールアドレスとパスワードを確認してください。'
        );
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Login handler error:', error);
      if (error.code === 'auth/invalid-credential') {
        setError(
          'メールアドレスまたはパスワードが正しくありません。Firebase Authenticationでユーザーが作成されているか確認してください。'
        );
      } else if (error.code === 'auth/user-not-found') {
        setError(
          'ユーザーが見つかりません。Firebase Authenticationでユーザーを作成してください。'
        );
      } else if (error.code === 'auth/wrong-password') {
        setError('パスワードが正しくありません。');
      } else {
        setError(`ログインに失敗しました: ${error.message || '不明なエラー'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* ヘッダー */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="bg-blue-100 p-3 rounded-full">
              <svg
                className="h-8 w-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">管理者ログイン</h1>
            <p className="text-gray-600">
              採用戦略分析システムにアクセスするには認証が必要です
            </p>
          </div>
        </div>

        {/* ログインフォーム */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-6 space-y-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-center text-gray-900">
                アカウントにログイン
              </h2>
              <p className="text-sm text-center text-gray-600">
                管理者アカウントでログインしてください
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  メールアドレス
                </label>
                <div className="relative">
                  <svg
                    className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <input
                    id="email"
                    type="email"
                    placeholder="admin@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-gray-700"
                >
                  パスワード
                </label>
                <div className="relative">
                  <svg
                    className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <input
                    id="password"
                    type="password"
                    placeholder="パスワードを入力"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'ログイン中...' : 'ログイン'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
